export const MAXIMIZES_REVENUE_DEMAND_CLASS_NAME = 'Yoga';

export const MAXIMIZES_REVENUE_DEMAND_CHECK_ICON = require('static/icons/smartrate-demand-check.svg');

// Positions derived from Figma Component 43 / Component 21 (165px circle, 32×27px user icon).
// Hexagonal layout per circle:
//        user2
//  user1       user3
//  user6       user4
//        user5
export const MAXIMIZES_REVENUE_DEMAND_SEATS = Object.freeze([
  Object.freeze({ id: 'seat-top-center', top: 22, left: 50 }),
  Object.freeze({ id: 'seat-left-upper', top: 34.2, left: 22.1 }),
  Object.freeze({ id: 'seat-right-upper', top: 34.2, left: 79.1 }),
  Object.freeze({ id: 'seat-left-lower', top: 59.6, left: 22.1 }),
  Object.freeze({ id: 'seat-right-lower', top: 59.6, left: 79.1 }),
  Object.freeze({ id: 'seat-bottom-center', top: 79, left: 50 }),
]);

const LEFT_FIGMA_USER_TO_SEAT = Object.freeze({
  user1: 'seat-left-upper',
  user2: 'seat-top-center',
  user3: 'seat-right-upper',
  user4: 'seat-right-lower',
  user5: 'seat-bottom-center',
  user6: 'seat-left-lower',
});

const RIGHT_FIGMA_USER_TO_SEAT = Object.freeze({
  user7: 'seat-left-upper',
  user8: 'seat-top-center',
  user9: 'seat-right-upper',
  user10: 'seat-right-lower',
  user11: 'seat-bottom-center',
  user12: 'seat-left-lower',
});

const seatsFromFigmaUsers = (userMap, users) => users.map(user => userMap[user]);

const ALL_LEFT = seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user1', 'user2', 'user3', 'user4', 'user5', 'user6']);
const ALL_RIGHT = seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, [
  'user7',
  'user8',
  'user9',
  'user10',
  'user11',
  'user12',
]);

export const MAXIMIZES_REVENUE_DEMAND_FRAME_MS = 450;
export const MAXIMIZES_REVENUE_DEMAND_HOLD_MS = 800;
export const MAXIMIZES_REVENUE_DEMAND_EARNINGS_MS = 2500;

/**
 * Animation timeline derived from Figma prototype interaction chain on component 43.
 *
 * Prototype loop: 22 → 23 → 24 → 36 → 37 → 38 → 39 → 40 → 42 → 26 → 27 → 28 → 29 → 30 → 31 → 32 → Variant20 → 21 → 22
 * We prepend 21 as an intro hold, then follow the chain through Variant20 before looping back to 21.
 */
export const MAXIMIZES_REVENUE_DEMAND_ANIMATION_FRAMES = Object.freeze([
  // 21 — full demand intro (prototype reset frame)
  Object.freeze({
    figmaVariant: 'Component 21',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: ALL_LEFT,
    rightVisible: ALL_RIGHT,
    holdMs: MAXIMIZES_REVENUE_DEMAND_HOLD_MS,
  }),
  // 22 — remove user2 (left)
  Object.freeze({
    figmaVariant: 'Component 22',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user1', 'user3', 'user4', 'user5', 'user6']),
    rightVisible: ALL_RIGHT,
  }),
  // 23 — remove user1 (left)
  Object.freeze({
    figmaVariant: 'Component 23',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5', 'user6']),
    rightVisible: ALL_RIGHT,
  }),
  // 24 — remove user6 (left)
  Object.freeze({
    figmaVariant: 'Component 24',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: ALL_RIGHT,
  }),
  // 36 — remove user8 (right)
  Object.freeze({
    figmaVariant: 'Component 36',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user7', 'user9', 'user10', 'user11', 'user12']),
  }),
  // 37 — remove user9 (right)
  Object.freeze({
    figmaVariant: 'Component 37',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user7', 'user10', 'user11', 'user12']),
  }),
  // 38 — remove user10 (right)
  Object.freeze({
    figmaVariant: 'Component 38',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user7', 'user11', 'user12']),
  }),
  // 39 — remove user7 (right)
  Object.freeze({
    figmaVariant: 'Component 39',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user11', 'user12']),
  }),
  // 40 — remove user12 (right)
  Object.freeze({
    figmaVariant: 'Component 40',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user11']),
  }),
  // 42 — 07:00 earnings ($36 vs $16), left circle green + checkmark
  Object.freeze({
    figmaVariant: 'Component 42',
    classTimeKey: 'first',
    demandSubtitleKey: 'highDemand',
    showEarnings: true,
    leftCircleVariant: 'green',
    rightCircleVariant: 'blue',
    checkmarkSide: 'left',
    lowerEarningsKey: 'first',
    higherEarningsKey: 'first',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user4', 'user5']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user11']),
    holdMs: MAXIMIZES_REVENUE_DEMAND_EARNINGS_MS,
  }),

  // ── 08:00 AM scene ──────────────────────────────────────────────────────

  // 26 — scene switch
  Object.freeze({
    figmaVariant: 'Component 26',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user1', 'user2', 'user3', 'user6']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user8', 'user9', 'user10', 'user12']),
    holdMs: 800,
  }),
  // 27 — remove user2 (left)
  Object.freeze({
    figmaVariant: 'Component 27',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user1', 'user3', 'user6']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user8', 'user9', 'user10', 'user12']),
  }),
  // 28 — remove user1 (left)
  Object.freeze({
    figmaVariant: 'Component 28',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3', 'user6']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user8', 'user9', 'user10', 'user12']),
  }),
  // 29 — remove user6 (left)
  Object.freeze({
    figmaVariant: 'Component 29',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user8', 'user9', 'user10', 'user12']),
  }),
  // 30 — remove user8 (right)
  Object.freeze({
    figmaVariant: 'Component 30',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user9', 'user10', 'user12']),
  }),
  // 31 — remove user9 (right)
  Object.freeze({
    figmaVariant: 'Component 31',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user10', 'user12']),
  }),
  // 32 — remove user10 (right)
  Object.freeze({
    figmaVariant: 'Component 32',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user12']),
  }),
  // Variant20 — 08:00 earnings ($12 vs $16), right circle green + checkmark
  Object.freeze({
    figmaVariant: 'Variant20',
    classTimeKey: 'second',
    demandSubtitleKey: 'lowDemand',
    showEarnings: true,
    leftCircleVariant: 'blue',
    rightCircleVariant: 'green',
    checkmarkSide: 'right',
    lowerEarningsKey: 'second',
    higherEarningsKey: 'first',
    leftVisible: seatsFromFigmaUsers(LEFT_FIGMA_USER_TO_SEAT, ['user3']),
    rightVisible: seatsFromFigmaUsers(RIGHT_FIGMA_USER_TO_SEAT, ['user12']),
    holdMs: MAXIMIZES_REVENUE_DEMAND_EARNINGS_MS,
  }),
]);

export const MAXIMIZES_REVENUE_DEMAND_REDUCED_MOTION_FRAME = MAXIMIZES_REVENUE_DEMAND_ANIMATION_FRAMES.find(
  frame => frame.figmaVariant === 'Component 42'
);
